import { useEffect, useRef, useState } from 'react'
import { Calendar } from 'lucide-react'

/**
 * DateField — input dd/mm/yyyy + calendar popover (single date).
 * Value/onChange folosesc ISO yyyy-mm-dd; UI afiseaza dd/mm/yyyy.
 */
export function DateField({
  label,
  value,
  onChange,
  placeholder = 'dd/mm/yyyy',
  hint,
}: {
  label?: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
  hint?: string
}) {
  const [open, setOpen] = useState(false)
  const [text, setText] = useState(isoToDdMmYyyy(value))
  /*
    Ce s-a scris in casuta nu se poate citi ca data. Pana acum nu se spunea nimic: campul ramanea
    cu textul omului, dar formularul pastra data veche (sau niciuna), iar salvarea esua sau
    inregistra altceva. Se arata dupa ce pleaca din camp, ca sa nu clipeasca rosu in timpul scrierii.
  */
  const [gresita, setGresita] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setText(isoToDdMmYyyy(value))
  }, [value])

  useClickOutside(wrapperRef, () => setOpen(false), open)

  return (
    <div ref={wrapperRef} className="relative">
      {label && (
        <label className="mb-1 block text-xs font-medium text-b4l-text-muted">{label}</label>
      )}
      <div className="relative">
        <input
          type="text"
          placeholder={placeholder}
          value={text}
          onChange={(e) => {
            const next = e.target.value
            setText(next)
            if (!next.trim()) {
              setGresita(false)
              onChange('')
              return
            }
            const iso = ddMmYyyyToIso(next)
            if (iso) {
              setGresita(false)
              onChange(iso)
            }
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setGresita(text.trim() !== '' && ddMmYyyyToIso(text) === null)}
          maxLength={10}
          className={`w-full rounded border bg-white px-3 py-2 pr-9 text-sm ${
            gresita ? 'border-b4l-red' : 'border-b4l-border'
          }`}
        />
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-1.5 text-b4l-text-muted hover:bg-b4l-bg-soft hover:text-b4l-text"
          aria-label="Deschide calendar"
        >
          <Calendar className="h-4 w-4" />
        </button>
      </div>
      {gresita && (
        <p className="mt-1 text-xs text-b4l-red">
          Data asta nu există. Scrie-o ca zi/lună/an, de pildă 30/09/2026.
        </p>
      )}
      {hint && !gresita && <p className="mt-1 text-[10px] text-b4l-text-muted">{hint}</p>}
      {open && (
        <CalendarPopover
          mode="single"
          isoValue={value}
          onPick={(iso) => {
            onChange(iso)
            setText(isoToDdMmYyyy(iso))
            setGresita(false)
            setOpen(false)
          }}
          onPickRange={() => {}}
          onClear={() => {
            onChange('')
            setText('')
            setGresita(false)
            setOpen(false)
          }}
        />
      )}
    </div>
  )
}

/**
 * DateRangeField — pereche From/To linkata, calendar comun cu vedere luni.
 * Click pe luna in vederea luni → fills both: from=ziua 1, to=ultima zi.
 */
export function DateRangeField({
  fromLabel = 'De la',
  toLabel = 'Până la',
  fromValue,
  toValue,
  onChange,
}: {
  fromLabel?: string
  toLabel?: string
  fromValue: string
  toValue: string
  onChange: (from: string, to: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [activeField, setActiveField] = useState<'from' | 'to'>('from')
  const [fromText, setFromText] = useState(isoToDdMmYyyy(fromValue))
  const [toText, setToText] = useState(isoToDdMmYyyy(toValue))
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => setFromText(isoToDdMmYyyy(fromValue)), [fromValue])
  useEffect(() => setToText(isoToDdMmYyyy(toValue)), [toValue])

  useClickOutside(wrapperRef, () => setOpen(false), open)

  const pickDay = (iso: string) => {
    if (activeField === 'from') {
      onChange(iso, toValue)
      setFromText(isoToDdMmYyyy(iso))
      // Trec automat la "to" pentru selectia continua
      setActiveField('to')
    } else {
      onChange(fromValue, iso)
      setToText(isoToDdMmYyyy(iso))
      setOpen(false)
    }
  }

  const pickMonth = (year: number, month: number) => {
    const firstDay = `${year}-${String(month + 1).padStart(2, '0')}-01`
    const lastDate = new Date(year, month + 1, 0)
    const lastDay = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDate.getDate()).padStart(2, '0')}`
    onChange(firstDay, lastDay)
    setFromText(isoToDdMmYyyy(firstDay))
    setToText(isoToDdMmYyyy(lastDay))
    setOpen(false)
  }

  return (
    <div ref={wrapperRef} className="relative">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="mb-1 block text-xs font-medium text-b4l-text-muted">{fromLabel}</label>
          <div className="relative">
            <input
              type="text"
              placeholder="dd/mm/yyyy"
              value={fromText}
              onChange={(e) => {
                const next = e.target.value
                setFromText(next)
                if (!next.trim()) onChange('', toValue)
                else {
                  const iso = ddMmYyyyToIso(next)
                  if (iso) onChange(iso, toValue)
                }
              }}
              onFocus={() => {
                setActiveField('from')
                setOpen(true)
              }}
              maxLength={10}
              className={`w-full rounded border bg-white px-3 py-2 pr-9 text-sm ${activeField === 'from' && open ? 'border-b4l-blue ring-1 ring-b4l-blue/30' : 'border-b4l-border'}`}
            />
            <button
              type="button"
              onClick={() => {
                setActiveField('from')
                setOpen((o) => !o)
              }}
              className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-1.5 text-b4l-text-muted hover:bg-b4l-bg-soft hover:text-b4l-text"
              aria-label="Deschide calendar"
            >
              <Calendar className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-b4l-text-muted">{toLabel}</label>
          <div className="relative">
            <input
              type="text"
              placeholder="dd/mm/yyyy"
              value={toText}
              onChange={(e) => {
                const next = e.target.value
                setToText(next)
                if (!next.trim()) onChange(fromValue, '')
                else {
                  const iso = ddMmYyyyToIso(next)
                  if (iso) onChange(fromValue, iso)
                }
              }}
              onFocus={() => {
                setActiveField('to')
                setOpen(true)
              }}
              maxLength={10}
              className={`w-full rounded border bg-white px-3 py-2 pr-9 text-sm ${activeField === 'to' && open ? 'border-b4l-blue ring-1 ring-b4l-blue/30' : 'border-b4l-border'}`}
            />
            <button
              type="button"
              onClick={() => {
                setActiveField('to')
                setOpen((o) => !o)
              }}
              className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-1.5 text-b4l-text-muted hover:bg-b4l-bg-soft hover:text-b4l-text"
              aria-label="Deschide calendar"
            >
              <Calendar className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
      {open && (
        <CalendarPopover
          mode="range"
          align={activeField === 'to' ? 'right' : 'left'}
          isoValue={activeField === 'from' ? fromValue : toValue}
          onPick={pickDay}
          onPickRange={pickMonth}
          onClear={() => {
            onChange('', '')
            setFromText('')
            setToText('')
            setOpen(false)
          }}
        />
      )}
    </div>
  )
}

function fmtLocalDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** Intervalul anului curent (YYYY-01-01 .. YYYY-12-31) — pentru filtru implicit. */
export function currentYearRange(): { from: string; to: string } {
  const y = new Date().getFullYear()
  return { from: `${y}-01-01`, to: `${y}-12-31` }
}

/**
 * DateQuickButtons — doar butoanele de selecție rapidă (Luna curentă / Luna
 * precedentă / Anul curent / Toate). Se pune pe un rând separat, deasupra
 * câmpurilor de dată, ca să nu strice alinierea într-un grid de filtre.
 */
export function DateQuickButtons({
  fromValue,
  toValue,
  onChange,
}: {
  fromValue: string
  toValue: string
  onChange: (from: string, to: string) => void
}) {
  const now = new Date()
  const presets = [
    {
      key: 'lunaCurenta',
      label: 'Luna curentă',
      from: new Date(now.getFullYear(), now.getMonth(), 1),
      to: new Date(now.getFullYear(), now.getMonth() + 1, 0),
    },
    {
      key: 'lunaPrecedenta',
      label: 'Luna precedentă',
      from: new Date(now.getFullYear(), now.getMonth() - 1, 1),
      to: new Date(now.getFullYear(), now.getMonth(), 0),
    },
    {
      key: 'anCurent',
      label: 'Anul curent',
      from: new Date(now.getFullYear(), 0, 1),
      to: new Date(now.getFullYear(), 11, 31),
    },
    {
      key: 'anPrecedent',
      label: 'An precedent',
      from: new Date(now.getFullYear() - 1, 0, 1),
      to: new Date(now.getFullYear() - 1, 11, 31),
    },
  ]
  const active =
    !fromValue && !toValue
      ? 'toate'
      : (presets.find((p) => fromValue === fmtLocalDate(p.from) && toValue === fmtLocalDate(p.to))
          ?.key ?? '')
  const cls = (on: boolean) =>
    `rounded-md border px-3 py-1.5 text-xs font-semibold shadow-sm transition ${
      on
        ? 'border-b4l-blue bg-b4l-blue text-white'
        : 'border-b4l-border bg-white text-b4l-text hover:border-b4l-blue hover:bg-b4l-blue-light/30 hover:text-b4l-blue-dark'
    }`

  return (
    <div className="flex flex-wrap gap-2">
      {presets.map((p) => (
        <button
          key={p.key}
          type="button"
          onClick={() => onChange(fmtLocalDate(p.from), fmtLocalDate(p.to))}
          className={cls(active === p.key)}
        >
          {p.label}
        </button>
      ))}
      {/*
        „Toata perioada", nu „Toate". Pe ecranul de Comenzi, butonul asta statea sub tab-urile de
        status, care au si ele un „Toate" — Alexandru: „nu inteleg filtrul Toate, ce semnifica?".
        Doua butoane cu acelasi nume, unul sub altul, filtrand lucruri diferite.
      */}
      <button type="button" onClick={() => onChange('', '')} className={cls(active === 'toate')}>
        Toată perioada
      </button>
    </div>
  )
}

/**
 * QuickDateRange — butoane preset (rând) + DateRangeField dedesubt. Pentru
 * contexte standalone (nu într-un grid de filtre).
 */
export function QuickDateRange({
  fromValue,
  toValue,
  onChange,
}: {
  fromValue: string
  toValue: string
  onChange: (from: string, to: string) => void
}) {
  return (
    <div>
      <div className="mb-2">
        <DateQuickButtons fromValue={fromValue} toValue={toValue} onChange={onChange} />
      </div>
      <DateRangeField fromValue={fromValue} toValue={toValue} onChange={onChange} />
    </div>
  )
}

const ZILE_RO = ['L', 'Ma', 'Mi', 'J', 'V', 'S', 'D']
const LUNI_RO = [
  'Ianuarie',
  'Februarie',
  'Martie',
  'Aprilie',
  'Mai',
  'Iunie',
  'Iulie',
  'August',
  'Septembrie',
  'Octombrie',
  'Noiembrie',
  'Decembrie',
]
const LUNI_SHORT = [
  'Ian',
  'Feb',
  'Mar',
  'Apr',
  'Mai',
  'Iun',
  'Iul',
  'Aug',
  'Sep',
  'Oct',
  'Noi',
  'Dec',
]

function CalendarPopover({
  mode,
  align = 'left',
  isoValue,
  onPick,
  onPickRange,
  onClear,
}: {
  mode: 'single' | 'range'
  align?: 'left' | 'right'
  isoValue: string
  onPick: (iso: string) => void
  onPickRange: (year: number, month: number) => void
  onClear: () => void
}) {
  const today = new Date()
  const initial = isoValue ? new Date(isoValue + 'T00:00:00') : today
  const [view, setView] = useState({ year: initial.getFullYear(), month: initial.getMonth() })
  const [showMonthGrid, setShowMonthGrid] = useState(false)

  const firstOfMonth = new Date(view.year, view.month, 1)
  const startWeekday = (firstOfMonth.getDay() + 6) % 7
  const daysInMonth = new Date(view.year, view.month + 1, 0).getDate()
  const totalCells = startWeekday + daysInMonth
  const rowCount = Math.ceil(totalCells / 7)
  const cells: Array<number | null> = []
  for (let i = 0; i < rowCount * 7; i++) {
    const day = i - startWeekday + 1
    cells.push(day >= 1 && day <= daysInMonth ? day : null)
  }

  const goPrev = () => {
    setView((v) =>
      v.month === 0 ? { year: v.year - 1, month: 11 } : { year: v.year, month: v.month - 1 },
    )
  }
  const goNext = () => {
    setView((v) =>
      v.month === 11 ? { year: v.year + 1, month: 0 } : { year: v.year, month: v.month + 1 },
    )
  }
  const isToday = (day: number) =>
    today.getFullYear() === view.year && today.getMonth() === view.month && today.getDate() === day
  const isSelected = (day: number) => {
    if (!isoValue) return false
    return (
      isoValue ===
      `${view.year}-${String(view.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    )
  }
  const pickDay = (day: number) => {
    const iso = `${view.year}-${String(view.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    onPick(iso)
  }

  return (
    <div
      className={`absolute z-30 mt-1 w-72 rounded-lg border border-b4l-border bg-white p-3 shadow-lg ${align === 'right' ? 'right-0' : 'left-0'}`}
    >
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button"
          onClick={goPrev}
          className="rounded p-1 text-b4l-text-muted hover:bg-b4l-bg-soft hover:text-b4l-text"
          aria-label="Luna precedentă"
        >
          ‹
        </button>
        <button
          type="button"
          onClick={() => setShowMonthGrid((s) => !s)}
          className="rounded px-2 py-0.5 text-sm font-medium hover:bg-b4l-bg-soft"
          title={mode === 'range' ? 'Apasă ca să selectezi toată luna' : 'Schimbă luna sau anul'}
        >
          {LUNI_RO[view.month]} {view.year}
        </button>
        <button
          type="button"
          onClick={goNext}
          className="rounded p-1 text-b4l-text-muted hover:bg-b4l-bg-soft hover:text-b4l-text"
          aria-label="Luna următoare"
        >
          ›
        </button>
      </div>

      {showMonthGrid ? (
        <>
          <div className="mb-2 flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => setView((v) => ({ ...v, year: v.year - 1 }))}
              className="rounded px-2 py-0.5 text-xs hover:bg-b4l-bg-soft"
            >
              ‹
            </button>
            <span className="text-sm font-semibold">{view.year}</span>
            <button
              type="button"
              onClick={() => setView((v) => ({ ...v, year: v.year + 1 }))}
              className="rounded px-2 py-0.5 text-xs hover:bg-b4l-bg-soft"
            >
              ›
            </button>
          </div>
          <div className="grid grid-cols-3 gap-1">
            {LUNI_SHORT.map((nm, i) => (
              <button
                key={i}
                type="button"
                onClick={() => {
                  if (mode === 'range') {
                    onPickRange(view.year, i)
                  } else {
                    setView({ year: view.year, month: i })
                    setShowMonthGrid(false)
                  }
                }}
                className={`rounded py-2 text-xs transition ${
                  i === view.month && view.year === initial.getFullYear()
                    ? 'bg-b4l-blue-light/40 text-b4l-blue-dark'
                    : 'hover:bg-b4l-bg-soft'
                }`}
              >
                {nm}
              </button>
            ))}
          </div>
          {mode === 'range' && (
            <p className="mt-2 text-[10px] text-b4l-text-muted">
              Apasă pe o lună ca să selectezi toată luna (prima – ultima zi).
            </p>
          )}
        </>
      ) : (
        <>
          <div className="grid grid-cols-7 gap-0.5 text-center text-[10px] font-medium uppercase text-b4l-text-dim">
            {ZILE_RO.map((z) => (
              <div key={z} className="py-1">
                {z}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-0.5">
            {cells.map((d, i) => {
              if (d == null) return <div key={i} />
              const sel = isSelected(d)
              const t = isToday(d)
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => pickDay(d)}
                  className={`rounded py-1 text-xs transition ${
                    sel
                      ? 'bg-b4l-blue text-white'
                      : t
                        ? 'bg-b4l-blue-light/40 text-b4l-blue-dark hover:bg-b4l-blue-light'
                        : 'hover:bg-b4l-bg-soft'
                  }`}
                >
                  {d}
                </button>
              )
            })}
          </div>
        </>
      )}

      <div className="mt-2 flex justify-between border-t border-b4l-border pt-2">
        <button
          type="button"
          onClick={() => {
            const t = new Date()
            const iso = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`
            onPick(iso)
          }}
          className="text-xs text-b4l-blue-dark hover:underline"
        >
          Astăzi
        </button>
        <button
          type="button"
          onClick={onClear}
          className="text-xs text-b4l-text-muted hover:text-b4l-red-dark"
        >
          Șterge
        </button>
      </div>
    </div>
  )
}

function useClickOutside(
  ref: React.RefObject<HTMLElement | null>,
  handler: () => void,
  enabled: boolean,
) {
  useEffect(() => {
    if (!enabled) return
    const onClick = (ev: MouseEvent) => {
      if (ref.current && !ref.current.contains(ev.target as Node)) handler()
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [ref, handler, enabled])
}

export function isoToDdMmYyyy(iso: string): string {
  if (!iso) return ''
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!m) return ''
  return `${m[3]}/${m[2]}/${m[1]}`
}

/**
 * `31/09/2026` trece de sablon, dar septembrie n-are 31 de zile. Fara verificarea din calendar,
 * ISO-ul „2026-09-31" pleca mai departe, iar JavaScript il rostogolea tacut in 1 octombrie —
 * evenimentul ajungea in baza cu alta data decat a scris omul.
 */
export function ddMmYyyyToIso(s: string): string | null {
  const m = s.trim().match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/)
  if (!m) return null
  const zi = Number(m[1])
  const luna = Number(m[2])
  const an = Number(m[3])
  const d = new Date(Date.UTC(an, luna - 1, zi))
  if (d.getUTCFullYear() !== an || d.getUTCMonth() !== luna - 1 || d.getUTCDate() !== zi) {
    return null
  }
  return `${an}-${String(luna).padStart(2, '0')}-${String(zi).padStart(2, '0')}`
}
